import { useEffect, useState } from "react";
import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from "reactstrap";

import { IModal } from "../StatusMessages";
import useHelpers from "Hooks/useHelpers";
import useAlertContext from "Hooks/useAlertContext";
import useFetch from "Hooks/useFetch";
import styles from "Pages/Admin/StatusMessages/Components/Modals.module.css";

interface IProps {
    getMessages: (ignore?: boolean) => Promise<void>;
    onSetModal: (modal: IModal) => void;
    modal: IModal;
    selectedRow: any;
    onSetSelectedRow: (row: any) => void;
}

export interface IStatusMessage {
    Id: string;
    Status_type: string;
    Expires_at: string;
    Status_message: string;
    Issued_at: string;
    Issued_by: string;
}

export default function EditModal({
    getMessages,
    onSetModal,
    modal,
    selectedRow,
    onSetSelectedRow,
}: IProps) {
    const [message, setMessage] = useState<IStatusMessage>({
        Id: "",
        Status_type: "",
        Expires_at: "",
        Status_message: "",
        Issued_at: "",
        Issued_by: "",
    });

    const { handleFormChange } = useHelpers();
    const { put } = useFetch();
    const { newAlert } = useAlertContext();

    // Set the message to the selected row when the modal opens
    useEffect(() => {
        if (!selectedRow || modal !== "edit") return;

        const statusType =
            (selectedRow?.original.status_type === "danger"
                ? "Error"
                : selectedRow.original.status_type) || "";

        const expiresAt = new Date(selectedRow.original.expires_at);
        const offset = expiresAt.getTimezoneOffset();
        // Subtract the offset then convert to ISO string, to get a date string
        // in the format yyyy-mm-ddThh:mm (no seconds, ms, or Z at the end) for
        // use in datetime-local input
        expiresAt.setMinutes(expiresAt.getMinutes() - offset);
        const formattedExpiresAt = expiresAt.toISOString().slice(0, 16);

        setMessage({
            Id: selectedRow?.original.id || "",
            Status_type: statusType,
            Expires_at: formattedExpiresAt,
            Status_message: selectedRow?.original.status_message || "",
            Issued_at: selectedRow?.original.issued_at || "",
            Issued_by: selectedRow?.original.issued_by || "",
        });
    }, [modal, selectedRow]);

    const toggle = () => {
        onSetModal("");
    };

    const getSelectInputBgColor = (type: string) => {
        switch (type) {
            case "Info":
                return "var(--bs-info)";
            case "Warning":
                return "var(--bs-warning)";
            case "Error":
                return "var(--bs-danger)";
            default:
                return "";
        }
    };

    const editMessage = async () => {
        // Add the offset back to the date before sending to back end
        const date = new Date(message.Expires_at);
        const dateString = (message.Expires_at = date.toISOString());

        const formatStatusType = (type: string) => {
            switch (type) {
                case "Info":
                    return "info";
                case "Warning":
                    return "warning";
                case "Error":
                    return "danger";
                default:
                    return "info";
            }
        };

        const editingMessage = {
            ...message,
            Expires_at: dateString,
            Status_type: formatStatusType(message.Status_type),
        };

        console.log(`Adding message: `, message);

        const response = await put(
            `/api/admin/system_status/${message.Id}`,
            editingMessage
        );

        let data: Response | IStatusMessage;
        try {
            data = await response.json();
        } catch {
            data = response;
        }
        console.log(`Edit status message response: `, data);

        if (response.ok) {
            newAlert({
                message: "Status message changes saved",
                color: "success",
            });
            getMessages();
        } else {
            newAlert({
                message: "Error editing status message",
                color: "danger",
            });
        }
        toggle();
        onSetSelectedRow(null);
    };

    return (
        <Modal className="addModal" toggle={toggle} isOpen={modal === "edit"}>
            <ModalHeader toggle={toggle}>Edit Status Message</ModalHeader>
            {selectedRow && (
                <ModalBody>
                    <Form>
                        {/* Type */}
                        <FormGroup row>
                            <Col className={styles.labelCol} sm={3}>
                                <Label id="type" type="select">
                                    Type:{" "}
                                </Label>
                            </Col>
                            <Col className={styles.labelCol} sm={9}>
                                <Input
                                    id="Status_type"
                                    name="Status_type"
                                    type="select"
                                    value={message.Status_type}
                                    onChange={(e) =>
                                        handleFormChange(e, setMessage)
                                    }
                                    style={{
                                        backgroundColor: getSelectInputBgColor(
                                            message.Status_type
                                        ),
                                    }}
                                    required
                                >
                                    <option value="Info" className={"bg-info"}>
                                        Info
                                    </option>
                                    <option
                                        value="Warning"
                                        className="bg-warning"
                                    >
                                        Warning
                                    </option>
                                    <option value="Error" className="bg-danger">
                                        Error
                                    </option>
                                </Input>
                            </Col>
                        </FormGroup>
                        {/* Expires At */}
                        <FormGroup row>
                            <Col className={styles.labelCol} sm={3}>
                                <Label id="Expires_at" type="select">
                                    Expires at:{" "}
                                </Label>
                            </Col>
                            <Col sm={9}>
                                <Input
                                    id="Expires_at"
                                    name="Expires_at"
                                    type="datetime-local"
                                    value={message.Expires_at}
                                    onChange={(e) =>
                                        handleFormChange(e, setMessage)
                                    }
                                ></Input>
                            </Col>
                        </FormGroup>{" "}
                        {/* Message */}
                        <FormGroup row>
                            <Col sm={3}>
                                <Label
                                    className={styles.labelCol}
                                    id="Status_message"
                                    type="select"
                                >
                                    Message:{" "}
                                </Label>
                            </Col>
                            <Col sm={9}>
                                <Input
                                    className={styles.textarea}
                                    id="Status_message"
                                    name="Status_message"
                                    type="textarea"
                                    value={message.Status_message}
                                    onChange={(e) =>
                                        handleFormChange(e, setMessage)
                                    }
                                    minLength={2}
                                    maxLength={3000}
                                    required
                                ></Input>
                            </Col>
                        </FormGroup>
                    </Form>
                </ModalBody>
            )}
            <ModalFooter>
                <Button color="primary" onClick={editMessage}>
                    Update
                </Button>
            </ModalFooter>
        </Modal>
    );
}
